// imports
import _get from 'lodash/get';
import _map from 'lodash/map';
import Config from '$root/Config';
import Collection from '$dependencies/Collection';
import TaxonomyModel from '$models/TaxonomyModel';
import { isFuture } from 'date-fns';
import { str } from 'ajv';

// private static properties

// class definition
export default class TaxonomyCollection extends Collection {
	// constructor
	constructor(args = {}) {
		// clone args to allow modifications
		args = Object.assign({}, args);
		args.Model = args.Model || TaxonomyModel;

		if(args.type) {
			args.config = Object.assign(args.config ? args.config : {}, {
				url: Config.getUrl(args.type),
			});
		}

		// call super constructor
		super(args);
	}

	parse(data) {
		const terms = _get(data, 'data');
		data = _map(terms, (item) => {
			// create model
			item.label = item.title || item.name || item.label;
			item.name = item.title || item.name;
			item.title = item.title || item.name;
			item.value = item.uuid ? item.uuid : item.machineName;
			item.id = item.uuid ? item.uuid : item.machineName;
			item.color = normalizeTermColor(item);
			item.postalCode = item.postalCode || item.code || item.postal_code || 0;
			item.code = item.postalCode || item.code || 0;
			item.categoriesLegal = item.categoriesLegal ?? item.legalInfoStepRequired ?? false;
			item.categoriesVolume = item.categoriesVolume ?? item.volumeStepRequired ?? false;
			item.legalInfoStepRequired = item.legalInfoStepRequired ?? item.categoriesLegal ?? false;
			item.volumeStepRequired = item.volumeStepRequired ?? item.categoriesVolume ?? false;
			const newModel = new this.Model().set(item);
			return newModel;
		});
		return super.parse(data);
	}

	toAVSList() {
		// convert this collection to array for AdvancedSelectComponent
		return collectionToAvSelectOptionList(this);
	}

	toAVTList() {
		return collectionToAvTreeSelectList(this);
	}

	getList(parent = undefined) {
		const subset = new this.constructor({ Model: this.Model });
		subset._models = modelsMatchingParent(this, parent);
		return subset;
	}

	getByCode(code) {
		return this.findWhere((item) => String(item.postalCode || item.code) === String(code));
	}

	getByTid(tid) {
		return this.findWhere((item) => item.tid === tid || item.machineName === tid);
	}

	getCrumb(model, prop = null) {
		// model could also be a model, which is also allowed
		// if model is string => get model by string
		// if model is model => get model by id of that model to make sure model has all the data required, could be a search query
		model = this.get(typeof model === 'string' ? model : model.id || model.uuid);

		// render crumb & return
		return renderCrumb(model, this, prop);
	}

	get codes() {
		return this.toArray(true)
			.map((item) => item.postalCode || item.code)
			.filter((value) => value !== undefined && value !== null && value !== '');
	}

	get dictionaryByTitle() {
		return this.toDictionary('title');
	}

	get dictionaryByName() {
		return this.toDictionary('name');
	}

	get dictionaryByTid() {
		return this.toDictionary('tid');
	}

	get treeStructure() {
		const items = this.toArray(true);
		const structure = [];
		//get all parents
		const parents = items.filter(item => {
			return !item.parent;
		});
		//loop each parent to find his children and put them intro the new structure
		parents.forEach(p => {
			const children = items.filter(item => {
				return item.parent === p.uuid;
			})
			const newParent = Object.assign(p, {children: children.length ? children : undefined});
			structure.push(newParent);
		});

		return structure;
	}

	get treeStructureByMachineName() {
		const items = this.toArray(true);
		const structure = [];
		//get all parents
		const parents = items.filter(item => {
			return !item.parent;
		});
		//loop each parent to find his children and put them intro the new structure
		parents.forEach(p => {
			//we also check if parent has a id else we assign it (we then use machinename as id)
			const children = items.filter(item => {
				return item.parent === p.machineName;
			})
			const newParent = Object.assign(p, {children: children.length ? children : undefined});
			structure.push(newParent);
		});

		return structure;
	}

	get labelDictionary() {
		const dict = {};
		this.toArray(true).forEach(item => {
			dict[item.machineName] = item.label;
		})
		return dict;
	}

	get colorDictionary() {
		const dict = {};
		this.toArray(true).forEach((item) => {
			if (!item.color) return;
			[
				item.machineName,
				item.uuid,
				item.id,
				item.value,
				item.title,
				item.label,
				item.name,
			]
				.filter(Boolean)
				.forEach((key) => {
					dict[key] = item.color;
				});
		});
		return dict;
	}
}

function normalizeTermColor(item = {}) {
	const raw =
		item.color ||
		item.hex ||
		item.hexColor ||
		item.colour ||
		item.color_hex ||
		item.field_color;

	if (!raw) return '';
	if (typeof raw === 'string') return raw;
	if (typeof raw === 'object') {
		return raw.color || raw.hex || raw.value || raw.hexadecimal || '';
	}
	return '';
}

function parentIdOf(parent) {
	if (parent == null || parent === '' || parent === 'virtual') return null;
	if (typeof parent === 'object') return parent.id || parent.uuid;
	return parent;
}

function collectionHasId(collection, id) {
	if (!id) return false;
	return collection._models.some((item) => item.id == id || item.uuid == id);
}

function modelsMatchingParent(collection, parentId) {
	const wanted = parentIdOf(parentId);
	return (collection._models || []).filter((item) => {
		const itemParent = parentIdOf(item.parent);
		if (!wanted) {
			return !itemParent || !collectionHasId(collection, itemParent);
		}
		return itemParent === wanted;
	});
}

function collectionToAvSelectOptionList(collection, parentId = undefined) {
	const options = modelsMatchingParent(collection, parentId).map((model) => ({
		id: model.id,
		title: model.name,
		name: model.name,
		options: collectionToAvSelectOptionList(collection, model.id),
		parentId: parentId,
		parent: parentId,
		categoriesLegal: model.categoriesLegal || model.legalInfoStepRequired,
		categoriesVolume: model.categoriesVolume || model.volumeStepRequired,
		model,
	}));
	return options.length ? options : null;
}

function collectionToAvTreeSelectList(collection, parentId = undefined) {
	const options = modelsMatchingParent(collection, parentId).map((model) => ({
		id: model.id,
		label: model.name,
		children: collectionToAvTreeSelectList(collection, model.id),
		model,
	}));

	if (options.length) {
		return options;
	}
}

function renderCrumb(model, collection, prop) {
	// block if model is not defined
	if (!model) return [];

	// render path by swimming upstream
	let parentModel;
	let id = parentIdOf(model.parent);
	const p = [prop ? model.get(prop) : model];

	while (id && (parentModel = collection.findWhere((item) => item.id == id || item.uuid == id))) {
		// keep model or prop of model if prop was provided
		p.unshift(prop ? parentModel.get(prop) : parentModel);

		id = parentIdOf(parentModel.parent);
	}

	return p;
}
